import { inStar } from '../../../gardener/sets/sheriff';
import type { PixelCanvas } from '../../../lib';
import { sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot } from './shared';

/**
 * The Peacemaker's shots (the Sheriff set's gun, ADR-0160 amended): a gold sheriff's star spinning (its points tipped
 * with balls), a crack of flame and gunsmoke at the muzzle, a ricochet's spark and a puff of desert dust where it lands;
 * and the flourish's Stetson tossed off (`sheriff_hat`) and a tumbleweed rolling by (`sheriff_tumbleweed`).
 */

/** A five-point star at angle `a` (radians), radius r round (cx, cy), its points tipped with balls. */
function star(x: number, y: number, cx: number, cy: number, r: number, a: number): 'y' | 'Y' | 'U' | null {
  // Turn the point back by `a` and ask the upright star.
  const [dx, dy] = [x - cx, y - cy];
  const [rx, ry] = [dx * Math.cos(-a) - dy * Math.sin(-a), dx * Math.sin(-a) + dy * Math.cos(-a)];
  for (let i = 0; i < 5; i++) {
    const t = a + (i / 5) * Math.PI * 2;
    if (Math.hypot(x - cx - Math.sin(t) * r, y - cy + Math.cos(t) * r) < 1.3) return 'y';
  }
  if (!inStar(rx, ry, 0, 0, r)) return null;
  return dx + dy < -1 ? 'y' : dx + dy > 2 ? 'U' : 'Y';
}

/** The star spinning a fifth of a turn over the frames (centred a little behind the pellet's head). */
const spin = (f: number) => (c: PixelCanvas) => paint(c, (x, y) => star(x, y, 18, 8, 6, (f / 4) * ((Math.PI * 2) / 5)));

/** A crack of flame, then gunsmoke rolling out of the muzzle and greying. */
const crack = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 7, y - 20];
    const d = Math.hypot(dx, dy);
    if (f === 0) {
      // A white heart, a gold cone forward.
      if (d < 2.5) return 'w';
      if (dx > 0 && Math.abs(dy) < 4 - dx * 0.25 && dx < 14) return dx < 6 ? 'y' : 'Y';
      return d < 5 ? 'y' : null;
    }
    const puffs =
      f === 1
        ? [[13, 20, 4]]
        : [
            [16, 19, 4.5],
            [22, 21, 3.5],
            [11, 21, 3],
          ];
    let near = Infinity;
    for (const [px, py, r] of puffs as [number, number, number][])
      near = Math.min(near, Math.hypot(x - px, y - py) / r);
    if (near >= 1) return null;
    if (f === 1 && near < 0.4) return 'y';
    return near < 0.6 ? '5' : (x + y) % 3 === 0 && f === 2 ? null : '4';
  });

/** The ricochet: a white spark, then gold sparks flung and a puff of tan dust spreading and settling. */
const ricochet = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    const d = Math.hypot(dx, dy);
    if (f === 0) return star(x, y, 22, 22, 7, 0.3) ? 'w' : d < 9 && Math.abs(dx * dy) < 2 ? 'y' : null;
    // Gold sparks flung up and out, falling.
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI * (0.15 + (i / 5) * 0.7);
      const r = 4 + f * 4;
      if (Math.hypot(x - 22 - Math.cos(a) * r, y - 22 - Math.sin(a) * r - f * f * 0.8) < 0.85)
        return f === 3 ? 'U' : 'y';
    }
    // Dust: a low lumpy cloud spreading and thinning.
    const R = 4 + f * 3;
    const lumps = R * (0.85 + 0.15 * Math.sin(Math.atan2(dy, dx) * 6 + f));
    const dl = Math.hypot(dx, (dy - f) * 1.5);
    if (dl < lumps && (f < 3 || (Math.floor(x) + Math.floor(y)) % 2 === 0))
      return dl < lumps * 0.5 ? 'e' : dx + dy < 0 ? 't' : 'm';
    return null;
  });

/** The Stetson tossed off (24 × 16, its brim's middle at (12, 10)): the crown, the team-less red band (its keys are
 * the team's, which a flourish has no team for), the brim curling up. */
const hat = () => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const u = x - 12;
    const top = 9 - 3 * (Math.abs(u) / 11) ** 3;
    if (Math.abs(u) < 11.5 && y > top && y < top + 2.5) return y < top + 1 ? 'e' : 'm';
    if (Math.abs(u) < 6 - (9 - y) * 0.15 && y > 1.5 && y <= 9) {
      if (y > 6.5) return 'r';
      if (Math.abs(u) < 0.6 && y < 4) return 'n';
      return u < 0 ? 't' : 'm';
    }
    return null;
  });

/** A tumbleweed (16 × 16, its middle (8, 8)): a ball of tangled dry twigs, turning a quarter by frame. */
const tumble = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 8, y - 8];
    const d = Math.hypot(dx, dy);
    if (d > 7) return null;
    const a = Math.atan2(dy, dx) + (f * Math.PI) / 8;
    // Twigs: loops crossing at angles, gaps between them.
    const twig = Math.abs(Math.sin(a * 3 + d * 0.9)) < 0.28 || Math.abs(Math.sin(a * 2 - d * 1.3 + 1)) < 0.22;
    if (d > 6) return twig ? 'n' : null;
    if (!twig) return d < 2.5 ? 'n' : null;
    return dx + dy < -2 ? 'e' : dx + dy < 3 ? 't' : 'm';
  });

export default [
  sprite({
    name: 'star_shot',
    ...shot,
    notes: `The Peacemaker's bullet (ADR-0160 amended): a gold sheriff's star (lit on its top-left, burnt orange at its
bottom-right), each of its five points tipped with a ball, spinning a fifth of a turn over 4 frames. Drawn upright.
Rubric (4×): S4 R5 P4 L4 C4 A4 F4.`,
    anims: { idle: { fps: 16, frames: frames4(spin) } },
  }),
  sprite({
    name: 'star_muzzle',
    ...muzzle,
    notes: `The Peacemaker's muzzle flash (ADR-0160 amended): a crack of flame, a white heart and a gold cone forward,
then grey gunsmoke rolling out and breaking up. Points right. Rubric (4×): S4 R4 P4 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(crack) } },
  }),
  sprite({
    name: 'star_burst',
    ...burst,
    notes: `The Peacemaker's hit (ADR-0160 amended): a ricochet, a white star flash, then gold sparks flung up and falling
and a low lumpy puff of tan desert dust spreading and thinning. 4 frames. Rubric (4×): S4 R4 P4 L4 C4 A4 F4.`,
    anims: { burst: { fps: 16, loop: false, frames: frames4(ricochet) } },
  }),
  sprite({
    name: 'sheriff_hat',
    group: 'fx',
    size: [24, 16],
    origin: [12, 10],
    density: 4,
    notes: `The Sheriff's knock-out Stetson (ADR-0160 amended): tan felt, its crown creased down the middle on a red band,
its wide brim curling up at both ends, lit along its top. Rubric (4×): S4 R4 P4 L4 C4 F4.`,
    anims: { idle: { fps: 1, frames: [hat()] } },
  }),
  sprite({
    name: 'sheriff_tumbleweed',
    group: 'fx',
    size: [16, 16],
    origin: [8, 8],
    density: 4,
    notes: `The Sheriff's knock-out tumbleweed (ADR-0160 amended): a ball of tangled dry twigs (tan lit on its top-left,
brown in its heart and at its rim, gaps between them), turning over 4 frames as it rolls. Rubric (4×): S4 R4 P4 L4 C4
A4 F4.`,
    anims: { roll: { fps: 10, frames: frames4(tumble) } },
  }),
];
